import { useCallback, useEffect, useState } from "react";
import Icon from "./Icon";
import { useAuth } from "../contexts/AuthContext";
import { getPopupAnnouncements, markAnnouncementsSeen } from "../api/announcementService";
import { registerAnnouncementDialog } from "../utils/announcement";
import { formatDate } from "../utils/formatters";

// Root-mounted host for the one-time "what's new" popup (admin-only, server
// driven) and any programmatic info popup via showInfo(). Mounted once in
// App.js, alongside <CredentialsDialog/>.
export default function AnnouncementDialog() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]); // items to show, FIFO
  // Server-driven popups still need one POST /seen to stop re-appearing. Info
  // popups opened via showInfo() are ephemeral and never call it.
  const [serverPending, setServerPending] = useState(false);

  const current = queue[0] || null;

  // Auto-fetch the unseen popup backlog for admins on sign-in.
  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    let alive = true;
    getPopupAnnouncements()
      .then((res) => {
        const items = res?.data || [];
        if (alive && items.length) {
          setQueue((q) => [...items, ...q]);
          setServerPending(true);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.role]);

  // Programmatic info popups push onto the queue.
  useEffect(() => {
    registerAnnouncementDialog((item) => setQueue((q) => [...q, item]));
    return () => registerAnnouncementDialog(null);
  }, []);

  const dismiss = useCallback(() => {
    setQueue((q) => q.slice(1));
    // First dismissal of the server backlog acknowledges all of it at once.
    if (serverPending) {
      setServerPending(false);
      markAnnouncementsSeen().catch(() => {});
    }
  }, [serverPending]);

  // Escape closes the current popup.
  useEffect(() => {
    if (!current) return undefined;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, dismiss]);

  if (!current) return null;

  return (
    <div style={S.overlay} onClick={dismiss}>
      <div style={S.card} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={S.head}>
          <div style={S.iconWrap}><Icon name="sparkles" size={18} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.title}>{current.title}</div>
            <div style={S.sub}>
              {current.version && <span className="badge badge-info" style={{ marginRight: 8 }}>v{current.version}</span>}
              {current.published_at ? formatDate(current.published_at) : "What's new"}
            </div>
          </div>
        </div>

        <div style={S.body}>{current.body}</div>

        <div style={S.footer}>
          {queue.length > 1 && (
            <span className="t-muted text-xs" style={{ alignSelf: "center" }}>
              {queue.length - 1} more
            </span>
          )}
          <button className="btn btn-primary" style={{ marginLeft: "auto" }} onClick={dismiss}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 4000,
    background: "rgba(15,23,42,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16,
  },
  card: {
    background: "var(--surface, #fff)", color: "var(--ink-900, #0f172a)",
    width: "100%", maxWidth: 460, borderRadius: 12,
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)", padding: 20,
    maxHeight: "85vh", display: "flex", flexDirection: "column",
  },
  head: { display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 },
  iconWrap: {
    flex: "0 0 auto", width: 36, height: 36, borderRadius: 8,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "var(--primary-50, #eff6ff)", color: "var(--primary, #2563eb)",
  },
  title: { fontWeight: 700, fontSize: 16 },
  sub: { fontSize: 12, color: "var(--ink-500, #64748b)", marginTop: 4 },
  body: {
    fontSize: 14, lineHeight: 1.6, color: "var(--ink-700, #334155)",
    whiteSpace: "pre-wrap", overflowY: "auto", paddingRight: 4,
  },
  footer: { display: "flex", gap: 8, marginTop: 18 },
};
